import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MatchKeyMatchableType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CSVImportExportButton } from '~components/CSVImportExport/CSVImportExportButton';
import { CSVImportExportModal } from '~components/CSVImportExport/CSVImportExportModal';
import { useCSVModal } from '~components/CSVImportExport/useCSVModal';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { FeatureFlags } from '~constants/featureFlags';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import useEntityCsvConfig from '~hooks/useCSVImportExport/useEntityCsvConfig';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { Site } from '~hooks/useSites/models';
import { useSites } from '~hooks/useSites/useSites';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import useCompanyMatchKeysQuery from '~queries/useCompanyMatchKeysQuery';
import useCompanyQuery from '~queries/useCompanyQuery';
import connection from '~services/connectionModule';
import { Paginated } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import { SitesMapView } from './components/SitesMapView';
import { SitesTableView } from './components/SitesTableView';
import { SitesViewTabs, useSitesViewTab } from './components/SitesViewTabs';
import { SitesView } from './constants';
import { useSiteFormV2 } from './site-form-v2/hooks';
import type { SiteSavedResult } from './site-form-v2/types';
import SiteDialog from './SiteDialog';
import SiteFilters from './SiteFilters';

const NO_COMPANY_PAGE: Paginated<Site> = {
  data: [],
  pagination: { limit: MAX_API_PAGE_SIZE, page: 1 },
};

const SitesManagement = observer(() => {
  const { companyAssetsStore, toasterStore, userStore } = useStores();
  const { getCompanySites, removeSite, toggleSiteStatus } = useSites();
  const { getAdditionalGeoFencesForSite } = useSiteFormV2();
  const { deleteCompanyShare } = useCompanyShares();
  const { searchValue, setSearchQueryValue } = useDataGridSearch();

  const userPermissions = useMemo(() => {
    return userStore.getPermissions();
  }, [userStore.getPermissions()]);

  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const selectedId = selectedSite?.id ?? null;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUpdatingSites, setIsUpdatingSites] = useState(false);
  const selectedCompany = userStore.currentCompanies[0] || userStore.userCompany;
  const selectedCompanyID = selectedCompany?.id;
  // currentCompanies only carries CompanyBasic, which has no timeFormat.
  const { isLoading: isCompanyLoading, timeFormat: companyTimeFormat } = useCompanyQuery({
    companyId: selectedCompanyID,
  });
  const matchKeysEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.matchKeysEnabled,
  });
  const showMatchKeys = matchKeysEnabled && userPermissions.canViewMatchKey;
  const { matchKeysByMatchableId } = useCompanyMatchKeysQuery({
    companyId: selectedCompanyID,
    isEnabled: showMatchKeys,
    matchableType: MatchKeyMatchableType.SITE,
  });

  // Get current view from URL
  const currentView = useSitesViewTab();

  const {
    goToPage: goToSitesPage,
    isFetching: isFetchingSites,
    pagination,
    reloadCurrentPage: reloadSitesPage,
    rows,
    startOver: startSitesOver,
  } = useCursorPagination<Site>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      selectedCompanyID
        ? getCompanySites({
            companyId: selectedCompanyID,
            cursor,
            filters: companyAssetsStore.siteFilters,
            limit,
            link,
            searchQuery: searchValue,
            signal,
          })
        : Promise.resolve(NO_COMPANY_PAGE),
    limit: MAX_API_PAGE_SIZE,
  });

  // restart triggers: `fetchPage` reads the current values itself, so the body doesn't
  useEffect(() => {
    void startSitesOver();
  }, [selectedCompanyID, searchValue, companyAssetsStore.siteFilters, startSitesOver]);

  const { config: sitesConfig, loadConfig: loadSitesConfig } = useEntityCsvConfig(
    'sites',
    selectedCompanyID || '',
  );

  const refreshSites = useCallback(() => void goToSitesPage(), [goToSitesPage]);

  const csvModal = useCSVModal({ onImportComplete: refreshSites });

  const onChangeQuickFilter = (searchQuery: string) => {
    setSearchQueryValue(searchQuery);
  };

  const handleClose = useCallback(() => setDialogOpen(false), []);

  const handleSiteSaved = ({ mode }: SiteSavedResult) => {
    void (mode === 'created' ? goToSitesPage() : reloadSitesPage());
  };

  const editRow = (id: string) => {
    setSelectedSite(rows.find((row) => row.id === id) ?? null);
    setDialogOpen(true);
  };

  const createNew = () => {
    setSelectedSite(null);
    setDialogOpen(true);
  };

  const deleteRow = (id: string) => {
    setSelectedSite(rows.find((row) => row.id === id) ?? null);
    setIsDeleteDialogOpen(true);
  };

  const deleteCallback = async () => {
    if (selectedSite?.isActive) {
      setIsDeleteDialogOpen(false);
      return;
    }

    if (selectedId) {
      setIsUpdatingSites(true);
      try {
        await removeSite(selectedId);
        void reloadSitesPage();
        setIsDeleteDialogOpen(false);
        setSelectedSite(null);
        toasterStore.push(
          alert(t('administration.site.site_deleted'), AlertTypes.success),
        );
      } catch {
        // `removeSite` already reported the failure; the dialog stays open
      } finally {
        setIsUpdatingSites(false);
      }
    }
  };

  const handleDeleteCompanyShare = useCallback(
    async (companyShareId: string) => {
      try {
        await deleteCompanyShare(companyShareId);
        void reloadSitesPage();
        toasterStore.push(
          alert(t('administration.site.share_removed'), AlertTypes.success),
        );
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.sites.failed_to_unshare'));
      }
    },
    [deleteCompanyShare, reloadSitesPage, toasterStore],
  );

  const toggleSite = async (id: string, isActive: boolean) => {
    setIsUpdatingSites(true);
    try {
      await toggleSiteStatus({ id, isActive });
      void reloadSitesPage();
      toasterStore.push(
        alert(t('administration.site.site_status_updated'), AlertTypes.success),
      );
    } finally {
      setIsUpdatingSites(false);
    }
  };

  const isSitesBusy = isFetchingSites || isUpdatingSites;
  const isDeleteConfirmButtonEnabled = !isUpdatingSites;
  const shouldShowDeleteCancelButton = !selectedSite?.isActive;

  // Callback for fetching additional geofences (used by map view)
  const handleGetAdditionalGeofences = useCallback(
    (companyId: string, siteId: string, signal?: AbortSignal) => {
      return getAdditionalGeoFencesForSite(companyId, siteId, signal);
    },
    [getAdditionalGeoFencesForSite],
  );

  return (
    <>
      <SettingsPageHeader
        title={t('administration.site.title')}
        description={t('settings.page_descriptions.sites')}
      />
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <SitesViewTabs />
            <TextField
              size="small"
              placeholder={`${t('form_fields.search')}...`}
              value={searchValue}
              onChange={(e) => onChangeQuickFilter(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: 'action.active' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchValue && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => onChangeQuickFilter('')}
                        edge="end"
                        aria-label="clear search"
                      >
                        <Close sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ width: 250 }}
            />
          </Box>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
            <SiteFilters />
            {userPermissions.canCreateSite && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <CSVImportExportButton onClick={csvModal.open} />
                <SmallButton
                  color="brandV2Yellow"
                  onClick={createNew}
                  startIcon={<Add />}
                >
                  {t('administration.site.add_new')}
                </SmallButton>
              </Box>
            )}
          </Box>
        </Box>

        {currentView === SitesView.TABLE ? (
          <SitesTableView
            sites={rows}
            isLoading={isSitesBusy || isCompanyLoading}
            matchKeysByMatchableId={matchKeysByMatchableId}
            showMatchKeys={showMatchKeys}
            pagination={pagination}
            timeFormat={companyTimeFormat}
            userPermissions={userPermissions}
            onEditSite={editRow}
            onDeleteSite={deleteRow}
            onToggleSiteStatus={toggleSite}
            onDeleteCompanyShare={handleDeleteCompanyShare}
            onPaginationChange={(link) => void goToSitesPage(link)}
          />
        ) : (
          <SitesMapView
            sites={rows}
            isLoading={isSitesBusy}
            onEditSite={editRow}
            getAdditionalGeofences={handleGetAdditionalGeofences}
            companyId={selectedCompanyID || ''}
            defaultMapCenter={
              // Use userCompany which has defaultLat/defaultLon (full Company type)
              // currentCompanies uses CompanyBasic which lacks these properties
              userStore.userCompany?.defaultLat && userStore.userCompany?.defaultLon
                ? {
                    lat: userStore.userCompany.defaultLat,
                    lng: userStore.userCompany.defaultLon,
                  }
                : undefined
            }
          />
        )}
      </Box>
      <SiteDialog
        isOpen={isDialogOpen}
        onClose={handleClose}
        onSuccess={handleSiteSaved}
        site={selectedSite ?? undefined}
        siteId={selectedId}
      />
      <Dialog
        isOpen={isDeleteDialogOpen}
        maxWidth="sm"
        onClose={() => setIsDeleteDialogOpen(false)}
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={() => setIsDeleteDialogOpen(false)}>
          {t('administration.site.delete_title')}
        </DialogHeader>

        <DialogContent>
          <Typography>
            {selectedSite?.isActive
              ? t('administration.site.unable_delete_active')
              : t('administration.site.delete_description')}
          </Typography>
        </DialogContent>

        <DialogActions>
          {shouldShowDeleteCancelButton ? (
            <SmallButton
              color="secondary"
              disabled={!isDeleteConfirmButtonEnabled}
              onClick={() => setIsDeleteDialogOpen(false)}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>
          ) : null}

          <SmallLoadingButton
            color={selectedSite?.isActive ? 'primary' : 'error'}
            disabled={!isDeleteConfirmButtonEnabled}
            loading={isUpdatingSites}
            onClick={deleteCallback}
          >
            {t(`actions.${selectedSite?.isActive ? 'ok' : 'confirm'}`)}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <CSVImportExportModal
        isOpen={csvModal.isOpen}
        onClose={csvModal.close}
        config={sitesConfig}
        loadConfig={loadSitesConfig}
        companyId={selectedCompanyID || ''}
        entityDisplayName={t('administration.site.title')}
        onImportComplete={csvModal.handleImportComplete}
      />
    </>
  );
});

export { SitesManagement };
